import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import CountdownTimer from 'adminDashboard/home/common/countdownTimer'
import moment from 'moment'

import { IntiateIchraRenewal, RenewalStatus, getPlanPeriodRenewalActivity, getRenewalPlanPeriod } from 'core'
import { User } from 'core/application/account/loginUser'
import { PlanPeriodDto } from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'
import { BenefitDesignStatus } from 'core/domain/benefit/benefitDesignStatus'

import { Button, Card, Col, Row, Skeleton } from 'common/components'
import { formatLongMonthAndDateOnly, formatYearV2 } from 'common/dateFormatters'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

type AdminCountdownTimerProps = {
  planPeriod: PlanPeriodDto
  user: User
}

const daysLeftForOpenEnrollment = () => {
  const startDate = moment()
  startDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
  const endDate = moment('01/11/' + startDate.year(), 'DD/MM/YYYY')
  return endDate.diff(startDate, 'days')
}

function IchraAdminCountdownTimer({ planPeriod, user }: AdminCountdownTimerProps) {
  const history = useHistory()

  const { t: translate } = useTranslation()
  const { data: renewalPlanPeriod, isLoading: isRenewalPlanPeriodLoading } = useQueryRequestV2({
    initialState: undefined,
    queryFn: () => getRenewalPlanPeriod(),
  })

  const { data: planPeriodRenewalActivity } = useQueryRequestV2({
    initialState: null,
    queryFn: () => getPlanPeriodRenewalActivity(),
  })

  const { sendCommand, isLoading: isCommandLoading } = useCommandRequestV2(({}) => IntiateIchraRenewal())

  const onInitiateRenewal = async () => {
    const result = await sendCommand({})
    if (result.completed) {
      history.push(ROUTE_PATHS.RENEWAL_TASKS)
    }
  }

  const nextYear = moment().add(1, 'years').year()

  const renewalYear = Number(renewalPlanPeriod?.startDate ? formatYearV2(renewalPlanPeriod?.startDate) : nextYear)

  const days = daysLeftForOpenEnrollment()

  const isAutoRenewed =
    planPeriodRenewalActivity !== null && planPeriodRenewalActivity?.status === RenewalStatus.Automatic

  const textStatusSwitch = () => {
    if (isAutoRenewed)
      return {
        header: <h4>Your benefit has auto-renewed for {renewalYear}. What now?</h4>,
        description: (
          <div>
            <p>
              Since you didn’t update or change your benefit settings prior to{' '}
              {formatLongMonthAndDateOnly(planPeriod.endDate)}, we’ve gone ahead and automatically launched your ICHRA
              for {renewalYear} using your current benefit settings. We’ve also notified your employees of their{' '}
              {renewalYear} plan offering.
            </p>
            <p>
              If you need to make changes to your {renewalYear} benefits design, you can do still do so through{' '}
              {formatLongMonthAndDateOnly(planPeriod.endDate)}.
            </p>
            <p>
              Please note that making changes at this time will require you to re-notify employees of their benefits
              change, which may adversely impact insurance coverage decisions that may have already been made.
            </p>
          </div>
        ),
        action: (
          <Button
            type="primary"
            onClick={() => {
              history.push(`${ROUTE_PATHS.BENEFIT_DETAILS_PATH}?planPeriodId=${renewalPlanPeriod.id}`)
            }}
          >
            Update your {renewalYear} benefits
          </Button>
        ),
      }
    else if (renewalPlanPeriod === null)
      return {
        header: <h4>The {renewalYear} benefit renewal window is open!</h4>,
        description: (
          <div>
            <p>
              You need to confirm that you plan to keep your existing ICHRA plan settings for {renewalYear} or make
              changes before December 1, {renewalYear - 1}.
            </p>
            <p>
              On December 1, {renewalYear - 1}, if you haven’t taken any action or properly completed the benefit
              renewal process, the system will automatically launch your {renewalYear} benefit and send an updated ICHRA
              Notification to your employees.
            </p>
          </div>
        ),
        action: (
          <Button type="primary" loading={isCommandLoading} disabled={isCommandLoading} onClick={onInitiateRenewal}>
            Renew for {renewalYear}
          </Button>
        ),
      }
    else if (renewalPlanPeriod !== null && renewalPlanPeriod?.notificationDate === null)
      return {
        header: <h4>You’ve started your {renewalYear} benefit renewal - keep going!</h4>,
        description: (
          <div>
            <p>
              You still need to confirm that you plan to keep your existing ICHRA plan settings for {renewalYear} or
              make changes before December 1, {renewalYear - 1}.
            </p>
            <p>
              On December 1, {renewalYear - 1}, if you haven’t taken any action or properly completed the benefit
              renewal process, the system will automatically launch your {renewalYear} benefit and send an updated ICHRA
              Notification to your employees.
            </p>
          </div>
        ),
        action: (
          <Button
            type="primary"
            onClick={() => {
              history.push(ROUTE_PATHS.RENEWAL_TASKS)
            }}
          >
            In Progress - Continue where you left off
          </Button>
        ),
      }
    else
      return {
        header: <h4>Congratulations! You’ve successfully renewed your benefit for {renewalYear}</h4>,
        description: (
          <div>
            <p>What’s next for your benefits?</p>
            <p>
              Now that your employees have received their {renewalYear} ICHRA Notice, they’ll have the ability to log-in
              and confirm or change their opt-in decision for {renewalYear}.
            </p>
            <p>
              We’ve found that getting an extra “nudge” from their plan admin can help an employee make the best
              decision for their situation, so if you want to review your team’s progress towards {renewalYear}, you can
              review your team member’s opt-in and premium reimbursement request status here on the admin dashboard, or
              on the Manage People page. “
            </p>
          </div>
        ),
        action: (
          <Button
            type="primary"
            onClick={() => {
              history.push(ROUTE_PATHS.MANAGE_PEOPLE)
            }}
          >
            Review employee statuses
          </Button>
        ),
      }
  }

  return (
    <Skeleton loading={isRenewalPlanPeriodLoading}>
      {(renewalPlanPeriod === null ||
        (renewalPlanPeriod !== null && renewalPlanPeriod?.status !== BenefitDesignStatus.Signed)) && (
        <Card style={{ backgroundColor: '#E6FFFF', border: '1px solid #8DF3FC' }}>
          <Row gutter={16}>
            <Col>
              <CountdownTimer days={days} />
            </Col>

            <Col xxl={18} xl={16} lg={13} md={13} sm={13} xs={24}>
              {textStatusSwitch().header}
              {textStatusSwitch().description}

              <Row gutter={16} className="mb-24 mt-24">
                <Col>{textStatusSwitch().action}</Col>
                <Col className="mt-4">
                  <a target={'_blank'} href="https://www.peoplekeep.com/state-open-enrollment">
                    <u>{translate('admin-dashboard.home.countdown-notification.link')}</u>
                  </a>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>
      )}
    </Skeleton>
  )
}

export default IchraAdminCountdownTimer
